<template>
    <div :class="$styleCrit.horizontal_layout">
        <div :class="$styleCrit.left_column">
            <Typography
                type="header"
                size="m"
                :responsive="false"
                :class="$styleCrit.title"
            >
                {{ tr('unavailableGame.oops') }}
            </Typography>
            <Typography
                type="label"
                size="s"
                :responsive="false"
                :class="$styleCrit.subtitle"
            >
                <!-- eslint-disable vue/no-v-html -->
                <p v-html="tr('unavailableGame.description')" />
            </Typography>
            <!-- eslint-disable vue/no-v-html -->
        </div>

        <div :class="$styleCrit.right_column">
            <div :class="$styleCrit.header">
                <Typography
                    type="label"
                    size="m"
                    semibold
                    :responsive="false"
                    :class="$styleCrit.title"
                >
                    {{ tr('unavailableGame.title') }}
                </Typography>
            </div>

            <div
                v-if="props.game"
                :class="$styleCrit.container"
            >
                <div :class="$styleCrit.content">
                    <div
                        :class="$styleCrit.cover_wrap"
                        @click="emit('clickPlay')"
                    >
                        <img
                            :src="props.game.preview"
                            :alt="props.game.title"
                            :class="$styleCrit.cover"
                        />
                    </div>
                    <div :class="$styleCrit.text">
                        <Typography
                            type="label"
                            size="s"
                            :responsive="false"
                            :class="$styleCrit.game_title"
                            @click="emit('clickPlay')"
                        >
                            {{ props.game.title }}
                        </Typography>
                    </div>
                    <Button
                        width="full"
                        color="purple-400"
                        :label="tr('gameContainer.play')"
                        interfaceTarget="unavailable-game-suggested-game-play"
                        :class="$styleCrit.button_desktop_override"
                        @click="emit('clickPlay')"
                    />
                    <PlayButton
                        interfaceTarget="unavailable-game-suggested-game-play"
                        :label="props.game ? $tr('gameContainer.play') : ''"
                        :class="$styleCrit.button_mobile_override"
                        @click="emit('clickPlay')"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import Button from '@/components/Buttons/Button.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'
import PlayButton from '@/pages/Games/components/PlayButton.vue'
import type { ShortGameType } from '@/types'

const props = defineProps<{
    game?: ShortGameType
    unavailableGameTitle?: string
}>()

const emit = defineEmits<{ clickPlay: [] }>()

const tr = useTr()
</script>

<style module="$styleCrit" critical>
.button_desktop_override {
    display: none;
}

.button_mobile_override {
    display: block;
}

@media (--mobile-landscape) {
    .button_desktop_override {
        display: none;
    }

    .button_mobile_override {
        display: none;
    }
}

.horizontal_layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 40px;
}

.left_column {
    padding-left: 32px;
    display: none;
    flex-direction: column;
    align-items: start;
    gap: 12px;
}

.right_column {
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 12px;
    align-items: start;
}

.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.container {
    width: 100%;
    backdrop-filter: blur(4px);
    background: #17171ae5;
    border: 1px solid rgba(255, 255, 255, 0.04);
    border-radius: var(--radius-24);
    display: flex;
    flex-direction: column;
    padding-bottom: 12px;
    color: white;
}

@media (--tablet-plus) {
    .horizontal_layout {
        grid-template-columns: 1fr 1fr;
        align-items: center;
        padding: 16px 16px 16px 0;
    }

    .left_column {
        display: flex;
    }

    .right_column {
        gap: 0;
    }

    .header {
        height: 44px;
        padding-left: 8px;
        background: rgba(42, 41, 53, 0.8);
        backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.04);
        border-bottom: none;
        border-radius: var(--radius-24) var(--radius-24) 0 0;
    }

    .container {
        width: 260px;
        background: rgba(42, 41, 53, 0.8);
        border-top: none;
        border-radius: 0 0 var(--radius-24) var(--radius-24);
    }

    .button_desktop_override {
        display: block;
    }

    .button_mobile_override {
        display: none;
    }
}

.title {
    flex: 1;
}

.subtitle {
    flex: 1;
    color: var(--white-60);
}

.content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 8px;
}

.cover_wrap {
    margin-top: 8px;
    aspect-ratio: 374 / 210;
    border-radius: var(--radius-16);
    overflow: hidden;
    width: 100%;
    cursor: pointer;
}

.cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.text {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.game_title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
}
</style>
